import React, { useCallback, useMemo, useState } from 'react';
import { addSongToSongCollection, getSongCollection, removeSongFromSongCollection, SongCollection } from '@round/api';
import TruncatedTextCell from '../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import { Popover } from '@round/ui-kit';
import Card from '../../../../ui/Card/Card';
import styles from './AddSongToSongCollection.module.css';
import useNonNullContext from '../../../../Hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/Options';
import { SelectBoxOptionWithId } from '../../../../ui/DataEntry/SelectBox/SelectBox.types.';
import CheckboxOption from '../../../../ui/DataEntry/SelectBox/components/CheckboxOption/CheckboxOption';
import OptionsList from '../../../../ui/DataEntry/SelectBox/components/OptionsList/OptionsList';
import { SongCollectionsListContext } from '../../contexts/SongCollectionsListContext';
import { showNotification } from '../../../../helpers';

type AddSongToSongCollectionProps = {
    songId: number;
    collectionIds: number[];
    renderAnchor?: (props: {
        selectedCollectionsCount: number;
        setRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
    }) => React.ReactNode;
};

const renderDefaultAnchor: AddSongToSongCollectionProps['renderAnchor'] = ({ setRef, selectedCollectionsCount }) => (
    <button className={styles.button} ref={setRef}>
        <TruncatedTextCell>
            {`Added to ${selectedCollectionsCount} collection${selectedCollectionsCount !== 1 ? 's' : ''}`}
        </TruncatedTextCell>
    </button>
);

const AddSongToSongCollection = ({
    collectionIds,
    songId,
    renderAnchor = renderDefaultAnchor,
}: AddSongToSongCollectionProps) => {
    const [popoverAnchorRef, setPopoverAnchorRef] = useState<HTMLElement | null>(null);
    const { clients } = useNonNullContext(OptionsContext);
    const {
        utils: { count, loading: collectionsLoading, hasNextPage, data: collections },
        search,
        setSearch,
        setPage,
    } = useNonNullContext(SongCollectionsListContext);

    const [initialCollectionsLoading, setInitialCollectionsLoading] = useState(false);
    const [initialCollectionIds, setInitialCollectionIds] = useState<number[]>(collectionIds);

    const [selectedCollections, setSelectedCollections] = useState<SelectBoxOptionWithId[] | null>(null);
    const collectionsCount = useMemo(
        () => (Array.isArray(selectedCollections) ? selectedCollections.length : initialCollectionIds.length),
        [initialCollectionIds.length, selectedCollections]
    );

    const mapCollectionToOption = useCallback(
        (c: SongCollection): SelectBoxOptionWithId => ({
            id: c.id,
            picture: c.picture ?? '',
            name: c.name,
            subtitle: clients.find((client) => client.id === c.client)?.name,
        }),
        [clients]
    );

    const options: SelectBoxOptionWithId[] = useMemo(() => {
        if (search) {
            return collections.map(mapCollectionToOption);
        }

        return collections
            .map(mapCollectionToOption)
            .concat(selectedCollections?.filter((s) => !collections.find((c) => c.id === s.id)) ?? [])
            .sort((a, b) => {
                return Number(initialCollectionIds.includes(b.id)) - Number(initialCollectionIds.includes(a.id));
            });
    }, [collections, initialCollectionIds, mapCollectionToOption, search, selectedCollections]);

    const onShowPopoverChange = useCallback(
        async (show: boolean) => {
            if (!show) {
                setInitialCollectionIds(selectedCollections?.map((c) => c.id) ?? collectionIds);
                return;
            }

            if (Array.isArray(selectedCollections)) {
                return;
            }

            try {
                setInitialCollectionsLoading(true);
                const responses = await Promise.all(collectionIds.map((id) => getSongCollection(id)));
                const songCollections = responses
                    .filter((response) => response.status === 200)
                    .map((response) => response.data as SongCollection);
                setSelectedCollections(songCollections.map(mapCollectionToOption));
            } catch {
                // no-op
            } finally {
                setInitialCollectionsLoading(false);
            }
        },
        [collectionIds, mapCollectionToOption, selectedCollections]
    );

    const removeFromSongCollection = useCallback(
        async (songCollection: SelectBoxOptionWithId) => {
            try {
                const response = await removeSongFromSongCollection(songCollection.id, songId);
                if (response.status === 204) {
                    showNotification('Song removed', 'info');
                    setSelectedCollections(
                        (collections) => collections?.filter((c) => c.id !== songCollection.id) ?? null
                    );
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }
            } catch (e) {
                showNotification((e as Error).message, 'error');
            }
        },
        [songId]
    );

    const addToSongCollection = useCallback(
        async (songCollection: SelectBoxOptionWithId) => {
            try {
                const response = await addSongToSongCollection(songCollection.id, songId);
                if (response.status === 204) {
                    showNotification('Song added', 'info');
                    setSelectedCollections((collections) => collections?.concat(songCollection) ?? null);
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                if (response.status === 404) {
                    showNotification(response.data.detail, 'error');
                    return;
                }
            } catch (e) {
                showNotification((e as Error).message, 'error');
            }
        },
        [songId]
    );

    return (
        <div onClick={(e) => e.stopPropagation()}>
            {renderAnchor({ setRef: setPopoverAnchorRef, selectedCollectionsCount: collectionsCount })}
            <Popover
                anchorElement={popoverAnchorRef}
                showOn="click"
                options={{ placement: 'bottom-start' }}
                onShowPopoverChange={onShowPopoverChange}
            >
                <Card className={styles.dropdown}>
                    <OptionsList
                        searchable
                        searchPlaceholder="Search collections"
                        searchQuery={search}
                        setSearchQuery={(search) => {
                            setPage(1);
                            setSearch(search);
                        }}
                        options={options}
                        count={count}
                        loading={collectionsLoading || initialCollectionsLoading}
                        loadMore={async () => {
                            if (hasNextPage) {
                                setPage((page) => page + 1);
                            }
                        }}
                    >
                        {(props) => {
                            const checked = Boolean(selectedCollections?.find((v) => v.id === props.item?.id));
                            return (
                                <CheckboxOption
                                    {...props}
                                    checked={checked}
                                    onClick={(item, e) => {
                                        e.stopPropagation();
                                        if (!item) {
                                            return;
                                        }

                                        if (checked) {
                                            removeFromSongCollection(item);
                                            return;
                                        }

                                        addToSongCollection(item);
                                    }}
                                />
                            );
                        }}
                    </OptionsList>
                </Card>
            </Popover>
        </div>
    );
};

export default AddSongToSongCollection;
